/**
 * Envelopes and bills.
 *
 * One page, two kinds, chosen by the route — the anatomy of an envelope and a
 * bill is nearly identical and two near-identical screens would be two things to
 * keep in sync. The differences (a bill has a due day; an envelope does not) are
 * handled inside the editor.
 */

import { useState } from 'react';
import {
  categoriesFor,
  centsEqual,
  formatAmountInput,
  formatMoney,
  ITEM_COLORS,
  getPayDays,
  listedPayDays,
  monthlyAmount,
  ordinalDay,
  parseMoney,
  perPaycheckFromMonthly,
  sumCents,
  type BudgetItem,
  type BudgetItemBalance,
  type BudgetItemKind,
  type FundingSchedule,
} from '@alohamula/domain';
import { PageHeader } from '../components/AppShell';
import {
  Button,
  Dialog,
  EmptyState,
  Field,
  Input,
  MoneyInput,
  Section,
  SegmentedControl,
  Select,
  Toggle,
} from '../components/ui';
import { useToast } from '../components/ui/ToastProvider';
import { BudgetItemCard } from '../components/BudgetItemCard';
import { BalanceListDialog, BorrowDialog } from '../components/BorrowDialog';
import { colorVar } from '../budget/colors';
import {
  useCreateBudgetItem,
  useDeleteBudgetItem,
  useSetAllocations,
  useTrashTransaction,
  useUpdateBudgetItem,
} from '../api/queries';
import type { Budget } from '../budget/useBudget';
import { EyeIcon, PlusIcon } from '../components/icons';
import './pages.css';

const COPY = {
  envelope: {
    title: 'Envelopes',
    one: 'envelope',
    empty: 'Envelopes are the spending categories a paycheck funds — groceries, gas, eating out.',
    fundedLabel: 'Funded per paycheck',
    monthLabel: 'Total per month',
    wholeLabel: 'Amount',
    monthHint: 'What the whole month adds up to',
  },
  bill: {
    title: 'Bills',
    one: 'bill',
    empty: 'Bills are the fixed things that come due every month — rent, insurance, utilities.',
    fundedLabel: 'Set aside per paycheck',
    monthLabel: 'Whole bill per month',
    wholeLabel: 'Whole bill',
    monthHint: 'What the bill actually costs',
  },
} as const;

function scheduleHint(schedule: FundingSchedule): string {
  const days = getPayDays();
  switch (schedule) {
    case 'everyPaycheck':
      return `Split across ${listedPayDays(days)}`;
    case 'firstPaycheck':
      return `All of it out of the ${ordinalDay(days[0] ?? 1)}`;
    case 'secondPaycheck':
      return `All of it out of the ${ordinalDay(days[1] ?? 16)}`;
    case 'onDay':
      return 'All of it from the paycheck that covers a chosen day';
  }
}

type KindFilter = 'all' | BudgetItemKind;
type ActivityFilter = 'all' | 'spent' | 'notSpent' | 'overspent';

interface BudgetItemsPageProps {
  budget: Budget;
}

export function BudgetItemsPage({ budget }: BudgetItemsPageProps) {
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  const [activity, setActivity] = useState<ActivityFilter>('all');
  const [editing, setEditing] = useState<BudgetItem | 'new' | null>(null);
  const [creatingKind, setCreatingKind] = useState<BudgetItemKind>('envelope');
  const [balancing, setBalancing] = useState(false);
  const [showingAccumulated, setShowingAccumulated] = useState(false);
  const pin = useUpdateBudgetItem();
  const toast = useToast();

  const allBalances = [...budget.envelopes, ...budget.bills].sort((a, b) =>
    a.item.name.localeCompare(b.item.name, undefined, { numeric: true, sensitivity: 'base' })
  );
  const balances = allBalances.filter((balance) => {
    if (kindFilter !== 'all' && balance.item.kind !== kindFilter) return false;
    if (activity === 'spent') return balance.spent > 0.005;
    if (activity === 'notSpent') return balance.spent <= 0.005;
    if (activity === 'overspent') return balance.isOverBudget;
    return true;
  });
  const kind: BudgetItemKind =
    editing === 'new' ? creatingKind : editing?.kind ?? creatingKind;

  const headlineBalances = balances.filter(
    (balance) =>
      !balance.item.parentId || !balances.some((candidate) => candidate.item.id === balance.item.parentId)
  );
  const spent = sumCents(headlineBalances.map((balance) => balance.spent));
  const remaining = sumCents(headlineBalances.map((balance) => balance.remaining));
  const accumulated = sumCents(balances.map((balance) => balance.carriedIn));
  const accumulations = balances
    .filter((balance) => Math.abs(balance.carriedIn) > 0.005)
    .sort((a, b) => Math.abs(b.carriedIn) - Math.abs(a.carriedIn));
  const fundCount = balances.filter((balance) => balance.item.isFund).length;
  const shortItems = allBalances
    .filter((balance) => balance.remaining < -0.005)
    .sort((a, b) => a.remaining - b.remaining);

  return (
    <>
      <PageHeader
        title="Envelopes & Bills"
        period={budget.period}
        action={
          <div className="row" style={{ gap: 'var(--space-xs)' }}>
            {shortItems.length > 0 && (
              <Button onClick={() => setBalancing(true)}>Balance</Button>
            )}
            <Button
              emphasis="primary"
              onClick={() => {
                setCreatingKind('envelope');
                setEditing('new');
              }}
            >
              <PlusIcon width={16} height={16} />
              New envelope
            </Button>
            <Button
              onClick={() => {
                setCreatingKind('bill');
                setEditing('new');
              }}
            >
              New bill
            </Button>
          </div>
        }
      />

      <div className="filters">
        <SegmentedControl
          label="Kind"
          value={kindFilter}
          options={[
            { value: 'all', label: 'All' },
            { value: 'envelope', label: 'Envelopes' },
            { value: 'bill', label: 'Bills' },
          ]}
          onChange={setKindFilter}
        />
        <SegmentedControl
          label="Activity"
          value={activity}
          options={[
            { value: 'all', label: 'All' },
            { value: 'spent', label: 'Spent' },
            { value: 'notSpent', label: 'Nothing' },
            { value: 'overspent', label: 'Over' },
          ]}
          onChange={setActivity}
        />
      </div>

      <div className="card headline headline--plan">
        <div className="headline__pair">
          <div className="figure">
            <span className="figure__label">Spent</span>
            <span className="figure__value figure__value--spent">{formatMoney(spent)}</span>
          </div>
          <div className="figure figure--end">
            <span className="figure__label">Remaining</span>
            <span className="figure__value">{formatMoney(remaining)}</span>
          </div>
        </div>
        <button
          type="button"
          className="headline__rolled"
          onClick={() => setShowingAccumulated(true)}
        >
          <span className="headline__rolled-label">
            Accumulated
            <EyeIcon width={14} height={14} />
          </span>
          <span className="headline__rolled-value">{formatMoney(accumulated)}</span>
        </button>
      </div>

      <Section
        title="A–Z"
        meta={
          fundCount > 0
            ? `${balances.length} · ${fundCount} ${fundCount === 1 ? 'fund' : 'funds'}`
            : `${balances.length}`
        }
      >
        {balances.length === 0 ? (
          <div className="card">
            <EmptyState
              title="Nothing matches"
              body="Try another filter, or add an envelope or bill."
              action={
                <Button
                  emphasis="primary"
                  onClick={() => {
                    setCreatingKind('envelope');
                    setEditing('new');
                  }}
                >
                  New envelope
                </Button>
              }
            />
          </div>
        ) : (
          <div className="items">
            {nestBalances(balances).map((row) => (
              <BudgetItemCard
                key={row.balance.item.id}
                balance={row.balance}
                depth={row.depth}
                onClick={() => setEditing(row.balance.item)}
                onTogglePin={async () => {
                  try {
                    await pin.mutateAsync({
                      id: row.balance.item.id,
                      input: {
                        isPinned: !row.balance.item.isPinned,
                        expectedVersion: row.balance.item.version,
                      },
                    });
                    toast.show(
                      row.balance.item.isPinned
                        ? `Unpinned ${row.balance.item.name}`
                        : `Pinned ${row.balance.item.name} to Overview`
                    );
                  } catch (error) {
                    toast.show(
                      error instanceof Error ? error.message : 'Could not update that pin.'
                    );
                  }
                }}
              />
            ))}
          </div>
        )}
      </Section>

      <Dialog
        open={showingAccumulated}
        title="Accumulated"
        subtitle="Leftover a fund kept from last paycheck. Regular envelopes and bills start fresh."
        onClose={() => setShowingAccumulated(false)}
        footer={
          <Button emphasis="primary" onClick={() => setShowingAccumulated(false)}>
            Done
          </Button>
        }
      >
        <p className="accumulate__total">{formatMoney(accumulated)}</p>
        {accumulations.length === 0 ? (
          <EmptyState title="Nothing accumulated" body="No funds carried leftover from last paycheck." />
        ) : (
          <div className="accumulate__list">
            {accumulations.map((balance) => (
              <div className="accumulate__row" key={balance.item.id}>
                <span className="accumulate__name">{balance.item.name}</span>
                <span className="accumulate__kind">
                  {balance.item.kind === 'bill' ? 'Bill' : 'Envelope'}
                </span>
                <span className="accumulate__amount">{formatMoney(balance.carriedIn)}</span>
              </div>
            ))}
          </div>
        )}
      </Dialog>

      {balancing && (
        <BalanceListDialog
          shortItems={shortItems}
          donors={allBalances}
          date={budget.period.start}
          onClose={() => setBalancing(false)}
        />
      )}

      {editing && (
        <BudgetItemEditor
          budget={budget}
          kind={kind}
          item={editing === 'new' ? null : editing}
          roots={allBalances
            .map((balance) => balance.item)
            .filter(
              (candidate) =>
                candidate.kind === kind &&
                !candidate.parentId &&
                candidate.id !== (editing === 'new' ? undefined : editing.id)
            )}
          balance={
            editing === 'new'
              ? null
              : balances.find((candidate) => candidate.item.id === editing.id) ?? null
          }
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

function nestBalances(balances: BudgetItemBalance[]): { balance: BudgetItemBalance; depth: number }[] {
  const visible = new Set(balances.map((balance) => balance.item.id));
  const children = new Map<string, BudgetItemBalance[]>();
  const roots: BudgetItemBalance[] = [];
  for (const balance of balances) {
    const parentId = balance.item.parentId;
    if (parentId && visible.has(parentId)) {
      const list = children.get(parentId) ?? [];
      list.push(balance);
      children.set(parentId, list);
    } else {
      roots.push(balance);
    }
  }
  const byName = (a: BudgetItemBalance, b: BudgetItemBalance) =>
    a.item.name.localeCompare(b.item.name, undefined, { numeric: true, sensitivity: 'base' });
  const rows: { balance: BudgetItemBalance; depth: number }[] = [];
  for (const root of roots.sort(byName)) {
    rows.push({ balance: root, depth: 0 });
    for (const child of (children.get(root.item.id) ?? []).sort(byName)) {
      rows.push({ balance: child, depth: 1 });
    }
  }
  return rows;
}

interface EditorProps {
  budget: Budget;
  kind: BudgetItemKind;
  item: BudgetItem | null;
  roots: BudgetItem[];
  balance: BudgetItemBalance | null;
  onClose: () => void;
}

function BudgetItemEditor({ budget, kind, item, roots, balance, onClose }: EditorProps) {
  const categories = categoriesFor(kind);
  const [name, setName] = useState(item?.name ?? '');
  /*
   * One typed number, and which of the two amount fields it was typed into. The
   * other field is derived from it, so both stay true to each other without a
   * second source of truth to keep in sync.
   */
  const [entry, setEntry] = useState<{ text: string; basis: 'month' | 'paycheck' }>(() => ({
    text: item ? formatAmountInput(item.perPaycheckAmount) : '',
    basis: 'paycheck',
  }));
  const [schedule, setSchedule] = useState<FundingSchedule>(
    item?.fundingSchedule ?? 'everyPaycheck'
  );
  const [category, setCategory] = useState(item?.category ?? categories[0]);
  const [color, setColor] = useState(item?.color ?? 'green');
  const [isFund, setIsFund] = useState(item?.isFund ?? false);
  const [dueDay, setDueDay] = useState(item?.dueDay != null ? String(item.dueDay) : '1');
  const [parentId, setParentId] = useState(item?.parentId ?? '');
  const [borrowing, setBorrowing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleteMoveTo, setDeleteMoveTo] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  /** Validation stays quiet until the first save attempt, then stays visible. */
  const [attempted, setAttempted] = useState(false);

  const create = useCreateBudgetItem();
  const update = useUpdateBudgetItem();
  const remove = useDeleteBudgetItem();
  const setAllocations = useSetAllocations();
  const trash = useTrashTransaction();
  const toast = useToast();

  const split = schedule === 'everyPaycheck';
  const typedAmount = parseMoney(entry.text);
  const parsedAmount =
    typedAmount == null
      ? null
      : entry.basis === 'month'
        ? perPaycheckFromMonthly(typedAmount, schedule)
        : typedAmount;
  const monthText =
    entry.basis === 'month'
      ? entry.text
      : parsedAmount == null
        ? ''
        : formatAmountInput(monthlyAmount(parsedAmount, schedule));
  const paycheckText =
    entry.basis === 'paycheck' ? entry.text : parsedAmount == null ? '' : formatAmountInput(parsedAmount);

  /*
   * Changing which paychecks fund it leaves the month's cost alone — rent does
   * not get cheaper because it stops arriving in halves — so the typed monthly
   * figure is kept and the per-paycheck one is recomputed from it.
   */
  function changeSchedule(next: FundingSchedule) {
    if (next === schedule) return;
    if (entry.basis === 'paycheck' && parsedAmount != null) {
      setEntry({ text: formatAmountInput(monthlyAmount(parsedAmount, schedule)), basis: 'month' });
    }
    setSchedule(next);
  }

  const nameError = name.trim().length === 0 ? 'Give it a name' : undefined;
  const trackingChild = parentId.length > 0;
  const amountError =
    entry.text.trim().length === 0 && !trackingChild
      ? 'Fill in either amount'
      : entry.text.trim().length > 0 && parsedAmount == null
        ? 'Use a number, like 250.00'
        : parsedAmount != null && parsedAmount < 0
          ? 'This cannot be negative'
          : undefined;
  /*
   * An odd-cent total has no exact half, so the rounded half funds a penny more
   * over the month than what was typed. Small, but it is the person's money and
   * it will not match their bill.
   */
  const roundingNote =
    entry.basis === 'month' &&
    typedAmount != null &&
    parsedAmount != null &&
    split &&
    !centsEqual(monthlyAmount(parsedAmount, schedule), typedAmount)
      ? `Splitting leaves ${formatMoney(parsedAmount)} each, which comes to ${formatMoney(monthlyAmount(parsedAmount, schedule))} a month.`
      : undefined;

  const busy =
    create.isPending || update.isPending || remove.isPending || setAllocations.isPending || trash.isPending;

  async function save() {
    setAttempted(true);
    const amount = trackingChild ? parsedAmount ?? 0 : parsedAmount;
    if (nameError || amountError || amount == null) return;
    setFailure(null);

    const shared = {
      name: name.trim(),
      perPaycheckAmount: amount,
      fundingSchedule: schedule,
      category,
      color,
      isFund,
      dueDay: kind === 'bill' ? Number(dueDay) : null,
      parentId: parentId || null,
    };

    try {
      if (item) {
        await update.mutateAsync({
          id: item.id,
          input: { ...shared, expectedVersion: item.version },
        });
        toast.show(`Saved ${shared.name}`);
      } else {
        await create.mutateAsync({
          ...shared,
          kind,
          icon: kind === 'bill' ? 'doc.text' : 'envelope',
          sortOrder: 0,
        });
        toast.show(`Added ${shared.name}`);
      }
      onClose();
    } catch (error) {
      // Conflicts happen for real here: the iOS app edits the same rows.
      setFailure(messageFor(error));
    }
  }

  const liveCharges = item
    ? budget.snapshot.allocations.filter((allocation) => {
        if (allocation.budgetItemId !== item.id) return false;
        const transaction = budget.snapshot.transactions.find(
          (candidate) => candidate.id === allocation.transactionId
        );
        return transaction != null && transaction.deletedAt == null;
      })
    : [];
  const liveChargeIds = [...new Set(liveCharges.map((allocation) => allocation.transactionId))];
  const count = liveChargeIds.length;
  const moveTargets = budget.activeItems.filter((candidate) => candidate.id !== item?.id);

  async function rewriteCharges(
    nextFor: (transactionId: string) => { budgetItemId: string; amount: number; notes?: string | null }[]
  ) {
    for (const transactionId of liveChargeIds) {
      const transaction = budget.snapshot.transactions.find((candidate) => candidate.id === transactionId);
      await setAllocations.mutateAsync({
        transactionId,
        allocations: nextFor(transactionId),
        expectedVersion: transaction?.version,
      });
    }
  }

  async function destroy(plan: 'empty' | 'move' | 'sort' | 'trash') {
    if (!item) return;
    setFailure(null);
    try {
      if (plan === 'empty' && count > 0) {
        setFailure('Move the charges or send them back to Needs sorting first.');
        return;
      }
      if (plan === 'move') {
        if (!deleteMoveTo) {
          setFailure('Pick where the charges should go.');
          return;
        }
        await rewriteCharges((transactionId) =>
          budget.snapshot.allocations
            .filter((allocation) => allocation.transactionId === transactionId)
            .map((allocation) => ({
              budgetItemId: allocation.budgetItemId === item.id ? deleteMoveTo : allocation.budgetItemId,
              amount: allocation.amount,
              notes: allocation.notes,
            }))
        );
      }
      if (plan === 'sort') {
        await rewriteCharges((transactionId) =>
          budget.snapshot.allocations
            .filter(
              (allocation) =>
                allocation.transactionId === transactionId && allocation.budgetItemId !== item.id
            )
            .map((allocation) => ({
              budgetItemId: allocation.budgetItemId,
              amount: allocation.amount,
              notes: allocation.notes,
            }))
        );
      }
      if (plan === 'trash') {
        for (const transactionId of liveChargeIds) {
          const all = budget.snapshot.allocations.filter(
            (allocation) => allocation.transactionId === transactionId
          );
          const onlyHere = all.every((allocation) => allocation.budgetItemId === item.id);
          const transaction = budget.snapshot.transactions.find((candidate) => candidate.id === transactionId);
          if (onlyHere && transaction) {
            await trash.mutateAsync({
              id: transactionId,
              expectedVersion: transaction.version,
              reason: 'Deleted with envelope',
            });
          } else {
            await setAllocations.mutateAsync({
              transactionId,
              allocations: all
                .filter((allocation) => allocation.budgetItemId !== item.id)
                .map((allocation) => ({
                  budgetItemId: allocation.budgetItemId,
                  amount: allocation.amount,
                  notes: allocation.notes,
                })),
              expectedVersion: transaction?.version,
            });
          }
        }
      }
      await remove.mutateAsync(item.id);
      toast.show(`Deleted ${item.name}`);
      onClose();
    } catch (error) {
      setFailure(messageFor(error));
    }
  }

  if (confirmingDelete && item) {
    return (
      <Dialog
        open
        title={`Delete ${item.name}?`}
        onClose={() => setConfirmingDelete(false)}
        footer={
          <>
            <div className="spacer" />
            <Button emphasis="ghost" onClick={() => setConfirmingDelete(false)} disabled={busy}>
              Keep it
            </Button>
            {count === 0 && (
              <Button emphasis="danger" loading={busy} onClick={() => void destroy('empty')}>
                Delete
              </Button>
            )}
            {count > 0 && (
              <Button
                emphasis="primary"
                loading={busy}
                disabled={!deleteMoveTo}
                onClick={() => void destroy('move')}
              >
                Move and delete
              </Button>
            )}
          </>
        }
      >
        {count === 0 ? (
          <p>
            Nothing is filed against it, so nothing else changes. Sub-envelopes under it stay and
            become top-level.
          </p>
        ) : (
          <div className="stack" style={{ gap: 'var(--space-md)' }}>
            <p>
              {count === 1 ? 'One charge is' : `${count} charges are`} filed here. Choose where{' '}
              {count === 1 ? 'it goes' : 'they go'}. This {item.kind} cannot be deleted until that is
              settled.
            </p>
            <Field label="Move to" hint="Keeps the charges. They count against the new envelope or bill.">
              {({ id }) => (
                <Select
                  id={id}
                  value={deleteMoveTo}
                  onChange={(event) => setDeleteMoveTo(event.target.value)}
                >
                  <option value="">Choose…</option>
                  {moveTargets.map((candidate) => (
                    <option key={candidate.id} value={candidate.id}>
                      {candidate.parentId
                        ? `${budget.itemsById.get(candidate.parentId)?.name ?? ''} › ${candidate.name}`
                        : candidate.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <div className="row" style={{ gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
              <Button emphasis="secondary" disabled={busy} onClick={() => void destroy('sort')}>
                Send back to Needs sorting
              </Button>
              <Button emphasis="danger" disabled={busy} onClick={() => void destroy('trash')}>
                Trash the charges too
              </Button>
            </div>
            <p className="field__hint">
              Needs sorting leaves the charges in the ledger as gaps to file again. Trash only
              takes charges filed entirely here; a split keeps its other parts.
            </p>
          </div>
        )}
        {failure && (
          <p className="field__error" role="alert">
            {failure}
          </p>
        )}
      </Dialog>
    );
  }

  if (borrowing && item && balance) {
    return (
      <BorrowDialog
        recipient={balance}
        donors={[...budget.envelopes, ...budget.bills].filter((row) => row.item.id !== item.id)}
        date={budget.period.start}
        onClose={() => setBorrowing(false)}
      />
    );
  }

  return (
    <Dialog
      open
      title={item ? item.name : `New ${COPY[kind].one}`}
      subtitle={
        balance
          ? `${formatMoney(balance.spent)} spent this period · ${formatMoney(balance.remaining)} left`
          : undefined
      }
      onClose={onClose}
      footer={
        <>
          {item && (
            <Button emphasis="danger" onClick={() => setConfirmingDelete(true)} disabled={busy}>
              Delete
            </Button>
          )}
          {item && balance && balance.remaining < -0.005 && (
            <Button onClick={() => setBorrowing(true)} disabled={busy}>
              Borrow
            </Button>
          )}
          <div className="spacer" />
          <Button emphasis="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          {/*
           * Deliberately not disabled on invalid input: a dead button with no
           * explanation is worse than one that presses and points at the problem.
           */}
          <Button emphasis="primary" loading={busy} onClick={() => void save()}>
            {item ? 'Save' : 'Add'}
          </Button>
        </>
      }
    >
      <div className="stack" style={{ gap: 'var(--space-md)' }}>
        {roots.length > 0 && (
          <Field
            label="Under"
            hint="Optional. Spend on a sub-envelope still counts against the parent."
          >
            {({ id }) => (
              <Select id={id} value={parentId} onChange={(event) => setParentId(event.target.value)}>
                <option value="">Top level</option>
                {roots.map((root) => (
                  <option key={root.id} value={root.id}>
                    {root.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}

        <Field label="Name" error={attempted ? nameError : undefined}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              value={name}
              placeholder={kind === 'envelope' ? 'Groceries' : 'Rent'}
              onChange={(event) => setName(event.target.value)}
              autoFocus
            />
          )}
        </Field>

        {/*
          * Both amounts, either one typeable. Pay lands twice a month, so funding
          * a $1,995 rent means storing $997.50 — arithmetic nobody should have to
          * do in another window, and redo every time the bill changes. Unless the
          * whole thing comes out of one paycheck, in which case there is only the
          * one figure and a second field would just echo the first.
          */}
        <div className="settings-grid">
          <Field label="Funded" hint={scheduleHint(schedule)}>
            {({ id }) => (
              <Select
                id={id}
                value={schedule}
                onChange={(event) => changeSchedule(event.target.value as FundingSchedule)}
              >
                <option value="everyPaycheck">Every paycheck</option>
                <option value="firstPaycheck">{ordinalDay(getPayDays()[0] ?? 1)} only</option>
                {getPayDays().length > 1 && (
                  <option value="secondPaycheck">{ordinalDay(getPayDays()[1] ?? 16)} only</option>
                )}
                {schedule === 'onDay' && <option value="onDay">On a chosen day</option>}
              </Select>
            )}
          </Field>

          <Field
            label={split ? COPY[kind].monthLabel : COPY[kind].wholeLabel}
            hint={COPY[kind].monthHint}
            warning={roundingNote}
            error={attempted && entry.basis === 'month' ? amountError : undefined}
          >
            {({ id, describedBy, invalid }) => (
              <MoneyInput
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                value={monthText}
                placeholder="0.00"
                onChange={(event) => setEntry({ text: event.target.value, basis: 'month' })}
              />
            )}
          </Field>

          {split && (
            <Field
              label="Each paycheck"
              hint={`Split across ${listedPayDays()}`}
              error={attempted && entry.basis === 'paycheck' ? amountError : undefined}
            >
              {({ id, describedBy, invalid }) => (
                <MoneyInput
                  id={id}
                  aria-describedby={describedBy}
                  aria-invalid={invalid || undefined}
                  value={paycheckText}
                  placeholder="0.00"
                  onChange={(event) => setEntry({ text: event.target.value, basis: 'paycheck' })}
                />
              )}
            </Field>
          )}
        </div>

        <div className="settings-grid">
          <Field label="Group">
            {({ id }) => (
              <Select id={id} value={category} onChange={(event) => setCategory(event.target.value)}>
                {categories.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          {kind === 'bill' && (
            <Field label="Due day" hint="Day of the month">
              {({ id }) => (
                <Select id={id} value={dueDay} onChange={(event) => setDueDay(event.target.value)}>
                  {Array.from({ length: 31 }, (_, index) => index + 1).map((day) => (
                    <option key={day} value={day}>
                      {day}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}
        </div>

        {/*
          * Sets the same flag the iOS app reads, so an item marked here is a fund
          * on the phone too.
          */}
        <Toggle
          label="Fund"
          hint={
            isFund
              ? 'Whatever is left at the end of a paycheck carries into the next one, so this can build up.'
              : `Starts fresh every paycheck, like ${kind === 'envelope' ? 'groceries' : 'rent'}.`
          }
          checked={isFund}
          onChange={setIsFund}
        />

        <fieldset className="swatches">
          <legend className="field__label">Color</legend>
          {ITEM_COLORS.map((option) => (
            <button
              key={option}
              type="button"
              className={`swatch${option === color ? ' swatch--on' : ''}`}
              style={{ background: colorVar(option) }}
              aria-label={option}
              aria-pressed={option === color}
              onClick={() => setColor(option)}
            />
          ))}
        </fieldset>

        {failure && (
          <p className="field__error" role="alert">
            {failure}
          </p>
        )}
      </div>
    </Dialog>
  );
}

function messageFor(error: unknown): string {
  return error instanceof Error ? error.message : 'Something went wrong. Try again.';
}
